import { useState, useEffect, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { CreditCard } from 'lucide-react';
import { pageCache } from '../utils/pageCache';
import { fmt, fmtCents, fmtCount, fmtPct } from '../utils/formatters';
import LoadingScreen from '../components/LoadingScreen';

// Money in — payments received for invoices the business sent. Its own
// stream, never mixed with register sales (spec 2026-09-24-three-streams).
// Four cards and nothing else: each one either names money to go get or a
// risk worth knowing, so the owner is not drowned in figures they won't use.

const URL = '/api/payments/summary';

function Card({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="bg-white border border-[#E6E2DE] rounded-[14px] p-6 shadow-card">
      <h3 className="font-serif text-lg text-[#2D2A2E] mb-3">{title}</h3>
      {children}
    </div>
  );
}

function Row({ left, right, sub }: { left: string; right: string; sub?: string }) {
  return (
    <div className="flex items-start justify-between gap-4 py-2.5">
      <div className="min-w-0">
        <div className="font-sans text-sm text-[#2D2A2E] truncate">{left}</div>
        {sub && <div className="font-sans text-xs text-[#6B6670] mt-0.5">{sub}</div>}
      </div>
      <div className="font-sans text-sm font-medium text-[#2D2A2E] tabular-nums shrink-0">{right}</div>
    </div>
  );
}

export default function PaymentsPage() {
  const [data, setData] = useState<any>(() => pageCache.get(URL) ?? null);
  const [loading, setLoading] = useState(() => !pageCache.has(URL));
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(URL)
      .then((res) => { pageCache.set(URL, res.data); setData(res.data); })
      .catch(() => { if (!pageCache.has(URL)) setData(null); })
      .finally(() => setLoading(false));
  }, []);

  const owed = data?.still_owed;
  const quiet = data?.going_quiet || [];
  const dep = data?.dependence;
  const fees = data?.fees;

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease: 'easeOut' }}>
      <div className="mb-8">
        <h1 className="font-serif text-2xl text-[#2D2A2E]">Payments</h1>
        {data?.has_data && (
          <p className="font-sans text-xs text-[#4A2D5E] mt-1.5">
            {fmt(data.total_in)} from {data.paid_count} payments, {data.date_range[0]} – {data.date_range[1]}
          </p>
        )}
      </div>

      {loading ? (
        <LoadingScreen fullScreen={false} />
      ) : !data?.has_data ? (
        <div className="flex flex-col items-center justify-center py-24">
          <CreditCard size={48} strokeWidth={1.5} className="text-[#4A2D5E]/30 mb-4" />
          <h3 className="font-serif text-xl text-[#2D2A2E] mb-2">No payments yet</h3>
          <p className="font-sans text-sm text-[#6B6670] mb-6 text-center max-w-sm">
            Upload a payments export (Stripe, QuickBooks, Square Invoices) and choose <span className="font-medium">Payments I received</span>.
          </p>
          <button onClick={() => navigate('/settings/data')} className="bg-[#2E6B4F] text-white hover:bg-[#245A41] rounded-[8px] shadow-sm font-sans font-medium px-6 py-2.5 transition-colors">
            Upload Data
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card title="Still owed">
            <div className="font-sans text-2xl font-bold text-[#2D2A2E] tabular-nums mb-1">{fmtCents(owed.total)}</div>
            <p className="font-sans text-xs text-[#6B6670] mb-2">Payments that failed and were never paid later.</p>
            {owed.items.length === 0 ? (
              <p className="font-sans text-sm text-[#6B6670]">Nothing outstanding.</p>
            ) : (
              <div className="divide-y divide-[#E6E2DE]">
                {owed.items.slice(0, 5).map((i: any, n: number) => (
                  <Row key={n} left={i.customer_label || 'Unknown customer'} right={fmtCents(i.amount)}
                       sub={[i.date, i.reason].filter(Boolean).join(' · ')} />
                ))}
              </div>
            )}
          </Card>

          <Card title="Going quiet">
            <p className="font-sans text-xs text-[#6B6670] mb-2">Regular customers who have stopped paying on their usual rhythm.</p>
            {quiet.length === 0 ? (
              <p className="font-sans text-sm text-[#6B6670]">Every regular customer is on rhythm.</p>
            ) : (
              <div className="divide-y divide-[#E6E2DE]">
                {quiet.slice(0, 5).map((q: any, n: number) => (
                  <Row key={n} left={q.customer_label} right={`${fmtCents(q.typical_amount)} usual`}
                       sub={`Usually pays every ${fmtCount(q.usual_every_days)} days — last paid ${q.last_paid}, ${fmtCount(q.days_since)} days before your newest payment`} />
                ))}
              </div>
            )}
          </Card>

          <Card title="Who you depend on">
            <div className="font-sans text-2xl font-bold text-[#2D2A2E] tabular-nums mb-1">{fmtPct(dep.top3_share * 100)}</div>
            <p className="font-sans text-xs text-[#6B6670] mb-2">of money in comes from your top three customers.</p>
            <div className="divide-y divide-[#E6E2DE]">
              {dep.top.map((t: any, n: number) => (
                <Row key={n} left={t.customer_label || 'Unknown customer'} right={fmtPct(t.share * 100)} sub={fmt(t.amount)} />
              ))}
            </div>
          </Card>

          {fees?.has_fees && (
            <Card title="What processing costs you">
              <div className="font-sans text-2xl font-bold text-[#2D2A2E] tabular-nums mb-1">{fmt(fees.total)}</div>
              <p className="font-sans text-xs text-[#6B6670]">in fees — {fmtPct(fees.rate * 100)} of what you were paid.</p>
            </Card>
          )}
        </div>
      )}
    </motion.div>
  );
}
